import React, { useState } from 'react';
import { useTranslations } from 'next-intl';
import { observer, inject } from 'mobx-react';
import { useRouter } from 'next/router';
import { formatPrice, adjustColor } from '@/utils/misc';
import { calculateCartTotals } from '@/utils/cartTotals';
import { syncCart } from '@/utils/cartSync';
import ButtonWithLoading from '@/components/form-fields/ButtonWithLoading';

const MiniCartTotals = inject('wooCommerceStore', 'studioStore', 'generalSettingsStore')(observer(({ wooCommerceStore, studioStore, generalSettingsStore }) => {

  const t = useTranslations('Cart');
  const productDesign = studioStore.cart.product;
  const primaryColor = studioStore.settings.colors.primary_color;
  const hoverPrimaryColor = adjustColor(primaryColor, -30);
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);

  const { total, totalSaved } = calculateCartTotals(wooCommerceStore.cart);
  const shippingCost = generalSettingsStore?.shippingMethods?.[0]?.cost ?? 0; // fallback prevents NaN
const handleCheckout = async (e) => {
   e.preventDefault();
  if (isLoading) return;           // 1️⃣ re-entrancy guard

  const syncData = wooCommerceStore.cartSyncData ?? {};
  const hasInvalid   = Array.isArray(syncData.invalidItems)   && syncData.invalidItems.length;
  const hasOutOfStock = Array.isArray(syncData.outOfStockItems) && syncData.outOfStockItems.length;
  const hasChanged   = Array.isArray(syncData.changedItems)   && syncData.changedItems.length;
  const hasChanges   = hasInvalid || hasOutOfStock || hasChanged;

  if (hasChanges && generalSettingsStore.showCartSyncModal) {
    try {
      setIsLoading(true);
      await syncCart({ preview: false });
    } finally {
      setIsLoading(false);
    }
     wooCommerceStore.setCartSyncModalOpen(true);
     return;
  }
  try {
    setIsLoading(true);
    wooCommerceStore.toggleCart();
    await router.push('/checkout-start');
  } catch (err) {
    console.error('Checkout navigation failed', err);
  } finally {
    // Component may unmount after push; safe-guard but ignore if unmounted
    setIsLoading(false);
  }
 };
  return (
    <div className={`mini-cart-totals p-2 px-4 md:p-4 md:mx-2 my-2 bg-primary-light-studio ${productDesign?.border_radius}`}>
      {totalSaved > 0 && (
        <div className="flex justify-between items-center md:my-1 text-secondary-studio">
          <span className="text-lg font-semibold">{t('total_saved')}</span>
          <span className="text-lg font-semibold">{formatPrice(totalSaved)}</span>
        </div>
      )}
      {shippingCost > 0 && (
        <div className="flex justify-between items-center md:my-1">
          <span className="text-lg font-semibold">{t('shipping')}</span>
          <span className="text-lg font-semibold">{formatPrice(shippingCost)}</span>
        </div>
      )}
      {total >= generalSettingsStore.minOrder ? (
        <ButtonWithLoading
          isLoading={isLoading}
          loadingText={t('loading')}
          text={
            <div className="flex justify-between items-center w-full">
              <span className="text-lg font-semibold">{t('proceed_to_checkout')}</span>
              <span className="text-lg font-semibold">{formatPrice(Number(total) + Number(shippingCost))}</span>
            </div>
          }
          className="payment-button w-full mt-3 md:mt-4 text-white font-bold py-2 px-4 rounded-lg transition duration-300"
          data-testid="proceed-to-checkout-button"
          onClick={handleCheckout}
        />
      ) : (
        <>
          <button
            onClick={wooCommerceStore.toggleMinOrderModal}
            className="payment-button w-full mt-4 text-white font-bold py-2 px-4 rounded-lg transition duration-300"
            data-testid="proceed-to-checkout-button"
          >
            <div className="flex justify-between items-center">
              <span className="text-lg font-semibold">{t('proceed_to_checkout')}</span>
              <span className="text-lg font-semibold">{formatPrice(Number(total) + Number(shippingCost))}</span>
            </div>
          </button>
        </>
      )}
      <style jsx>{`
        .payment-button {
          background-color: ${primaryColor}
        }
        .payment-button:hover {
          background-color: ${hoverPrimaryColor};
        }
      `}</style>
    </div>
  );
}));

export default MiniCartTotals;